/**
 * GameCore - Main game orchestration
 * 
 * Responsibilities:
 * - Renderer management
 * - Scene/camera setup
 * - Coordinating PlayerManager and GameLoop
 * - Voxel terrain integration
 * - Network event registration
 * 
 * Extracted responsibilities:
 * - Player lifecycle → PlayerManager
 * - RAF/timing/FPS → GameLoop
 */

import * as THREE from 'three';
import { createScene, getScene } from './scene/scene';
import { seamKindSummary } from './voxel/seamField.js';
import {
  createCamera, getCamera, updateCameraFromPlayer, updateSpectatorCamera,
  CAMERA_FAR_DEFAULT,
  CAMERA_NEAR,
} from './scene/camera';
import { initFirstPersonArm, updateFirstPersonArm, startFirstPersonArmExit, tickFirstPersonArmExit, renderFirstPersonArm } from './scene/FirstPersonArm';
import { startShaderWarmup, assertNoColdCompiles } from './render/ShaderWarmup';
import {
  initExploreCamera, updateExploreCamera, getExploreTarget, advanceExploreViewShift,
  advanceExploreTargetGlide, isExploreGliding, getExploreGlideDestination, getExploreDistance, isExploreMarkerInteracting,
  setExploreMaxBaseLod, getExploreZoomLevel, advanceExploreZoomTween, exploreCameraMove,
} from './scene/ExploreCamera';
import { updateLodZoom, resetLodZoom } from './lod/LodZoomPolicy';
import {
  initSpawnMarker, isMarkerPlaced, placeMarkerAtColumn, setMarkerVisible,
  getMarkerBase, consumeMarkerSpawn, resetMarker,
  getSpawnPosition as getMarkerSpawnPosition,
} from './spawn/SpawnMarker';
import { initLighting, applyEnvironmentSettings, updateShadowFollow, tickShadowUpdate, setShadowLodScale } from './scene/Lighting';
import { updateDayNightCycle } from './scene/DayNightCycle';
import { updateSkyTime, updateSkyCamera } from './scene/SkyDome';
import { initPostPipeline, disposePostPipeline, type PostPipeline } from './render/PostPipeline';
import { useGameStore } from '../state/store';
import { setClearChunksCallback, updateMapPlayerPosition, updateMapOtherPlayers, updatePhysicsHud } from '../state/transient';
import {
  QualityLevel,
  loadSavedQualityLevel,
  loadSavedFarLodLevels,
  detectQualityLevel,
  START_VIEW_DISTANCE,
  TOUCH_MAX_FAR_LOD_LEVELS,
  DEFAULT_FAR_LOD_LEVELS,
} from './quality/QualityPresets';
import { setRendererRef, setVisibilityRadiusCallback, setFarLodLevelsCallback, syncQualityToStore } from './quality/QualityManager';
import { controls } from './player/controls';
import { on } from '../net/decode';
import { RoomSnapshot, GameMode, VoxelBuildCommit, VoxelChunkData, BuildResult, MapTileResponse, SurfaceColumnResponse, RequestNack, updateTileFromChunk, updateTileHash, createMapTile, CHUNK_SIZE, VOXEL_SCALE, parseChunkKey, mapTileKey, parseMapTileKey, PLAYER_HEIGHT_INNER, PLAYER_RADIUS, MAX_BUILD_DISTANCE, RAY_SOLID, RAY_LIQUID, RAY_TRANSPARENT, type RaycastHit, type SubmersionResult } from '@worldify/shared';

/** Chebyshev radius (in map tiles = 8 m each) of map tiles kept around the player; farther explored
 *  tiles are evicted so the cache doesn't grow forever. ~48 tiles ≈ 384 m, ~28 MB worst case. */
const MAP_TILE_KEEP_RADIUS = 48;

/** Scratch for the punch-probe direction (updateFieldProbes runs every tick). */
const _probeDir = new THREE.Vector3();
import { VoxelIntegration } from './voxel/VoxelIntegration';
import type { VoxelWorld } from './voxel/VoxelWorld';
import { lodViewReachMetres } from './voxel/LodShellSelector';
import { createPhysicsWorldSource } from './voxel/physicsWorldAdapter';
import { setVoxelWireframe } from './voxel/VoxelMaterials';
import { GameLoop } from './GameLoop';
import { isTouch } from './deviceMode';
import { PlayerManager } from './PlayerManager';
import { Builder } from './build/Builder';
import { SpawnManager } from './spawn/SpawnManager';
import { materialManager, updateWindTime, subscribeMaterialSettings, subscribeWaterSettings } from './material';
import { getMapTileCache } from './maptile/mapTileCacheSingleton';
import { perfStats } from './debug/PerformanceStats';
import { GpuTimer } from './debug/GpuTimer';
import { initWorlds, setWorldSwitchHandler, loadSnapPoints, saveSnapPoints, savePlayerPos, loadPlayerPos, setPlayerPosProvider, saveTimeOfDay, loadTimeOfDay, setTimeOfDayProvider } from './world/WorldManager';

/** How often to persist the player position while Playing (ms). */
const POS_SAVE_INTERVAL_MS = 4000;
/** True when keystrokes belong to a focused text field, not to explore-mode WASD movement. */
function isTypingTarget(el: Element | null): boolean {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
    || (el instanceof HTMLElement && el.isContentEditable);
}

/** Cubic ease-in-out (0→1) for the explore↔first-person camera glides. */
function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * `detectQualityLevel` needs a GL context and runs before the renderer exists (the renderer's
 * depth mode depends on the result), so probe a throwaway one and release it.
 */
function detectQualityLevelWithProbeContext(): QualityLevel {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
  if (!gl) return 'medium';
  try {
    return detectQualityLevel(gl);
  } finally {
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}

export class GameCore {
  private renderer!: THREE.WebGLRenderer;

  // Extracted modules
  private gameLoop: GameLoop;
  private playerManager: PlayerManager;

  // Voxel terrain system
  private voxelIntegration!: VoxelIntegration;

  // Spawn system
  private spawnManager!: SpawnManager;

  // Build system
  private builder: Builder;

  // Track if player has been properly spawned
  private hasSpawnedPlayer = false;
  /** A marker spawn that arrived before its lod-0 terrain did — applied as soon as it is ready. */
  private pendingMarkerSpawn: THREE.Vector3 | null = null;
  private lastGameMode: GameMode = GameMode.MainMenu;
  
  // Center point for spectator camera orbit (updated when leaving Playing mode)
  private spectatorCenter = new THREE.Vector3(0, 0, 0);
  // Last tile the map cache was pruned around; prune only when the player crosses into a new tile so
  // explored map tiles don't accumulate without bound (see MapTileCache.prune).
  private mapPruneTx = NaN;
  private mapPruneTz = NaN;

  // Last (x,z) the explore center-follow placed the spawn marker at, so it only re-raycasts
  // when the camera target actually moved (NaN = force placement on the next frame).
  private lastFollowX = NaN;
  private lastFollowZ = NaN;

  // Throttle accumulator for periodic player-position saves while Playing (ms).
  private posSaveAccumMs = 0;

  /** How long physics must stay frozen before the player is told terrain is loading (ms). */
  private static readonly FROZEN_INDICATOR_DELAY_MS = 250;

  // True when the active world has a persisted position — spawn is ready immediately
  // (no terrain raycast needed) and streaming is centered on that saved position.
  private hasSavedSpawn = false;

  // Store subscription for the render-scale slider
  private renderScaleUnsub: (() => void) | null = null;
  private gpuTimer: GpuTimer | null = null;
  private postPipeline!: PostPipeline;

  // Last voxel stats written to the store (change-gate to avoid per-frame re-renders)
  private lastVoxelStats = { chunksLoaded: -1, meshesVisible: -1, debugObjects: -1, baseLod: -1,
    chunksDrawn: -1, lodStatus: '', meshChurn: '', wastePct: '', skirtKinds: '' };

  // Debounce for the "waiting for terrain" indicator: single-tick freezes are normal at the
  // streaming frontier and must not flash a message at the player.
  private frozenMs = 0;
  private frozenFlag = false;

  // Store subscriptions that push material/water settings to the shaders
  private materialSettingsUnsub: (() => void) | null = null;
  private waterSettingsUnsub: (() => void) | null = null;

  constructor() {
    this.gameLoop = new GameLoop();
    this.playerManager = new PlayerManager();
    this.builder = new Builder(controls);
  }

  async init(): Promise<void> {
    // Initialize material system early (non-blocking)
    materialManager.initialize().catch(err => {
      console.warn('Material initialization failed, using fallback colors:', err);
    });

    // ---- Quality auto-detect / restore ----
    // Resolved BEFORE the renderer exists: the depth-buffer mode below is a construction-time
    // option and depends on the preset.
    const savedLevel = loadSavedQualityLevel();
    let qualityLevel: QualityLevel;
    if (savedLevel) {
      qualityLevel = savedLevel;
      console.log(`[Quality] Restored saved preset: ${qualityLevel}`);
    } else {
      qualityLevel = detectQualityLevelWithProbeContext();
      console.log(`[Quality] Auto-detected preset: ${qualityLevel}`);
    }
    // THE VIEW STARTS NEAR, ALWAYS — see START_VIEW_DISTANCE. Not the preset's radius, not the
    // touch special case, not last session's choice: a cold start is dominated by view distance
    // (each step out is ~2.4x the chunks before anything draws), and a fast machine is not a reason
    // to spend that. One tap raises it.
    const effectiveVisibility = START_VIEW_DISTANCE;
    // Far-view LOD rings: saved override, else the one default (capped on touch — rings are cheap per
    // level but mobile keeps the smallest far view by default). Not the preset's: a preset is looks.
    const savedFarLods = loadSavedFarLodLevels();
    const effectiveFarLods = savedFarLods
      ?? (isTouch() ? Math.min(TOUCH_MAX_FAR_LOD_LEVELS, DEFAULT_FAR_LOD_LEVELS) : DEFAULT_FAR_LOD_LEVELS);

    // Create renderer
    // antialias: false — MSAA is `samples` on the post pipeline's scene target (render/PostPipeline.ts)
    // Plain z/w depth on every tier. Logarithmic depth writes gl_FragDepth in every fragment shader,
    // which disables early-Z: measured 52.6 → 38.5 ms/frame with it off on an Intel Iris Xe. The
    // precision it bought against a kilometres-deep far plane comes from the near plane instead
    // (updateCameraLodRange). Not reversed depth: postprocessing's SSAO linearises depth assuming
    // the standard mapping.
    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      powerPreference: 'high-performance',
    });
    this.gpuTimer = new GpuTimer(this.renderer);
    console.log(`[Renderer] GPU timer ${this.gpuTimer.available ? 'available' : 'unavailable (EXT_disjoint_timer_query_webgl2)'}`);
    // updateStyle=false: the drawing buffer may be rendered below native (renderScale);
    // CSS keeps the canvas full-size so the browser upscales it (the fill-rate win).
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    // DPI cap (the device-pixel lever). `renderScale` separately scales the sub-native
    // buffer; there is no per-preset maxPixelRatio anymore.
    const MAX_DEVICE_PIXEL_RATIO = 2;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_DEVICE_PIXEL_RATIO));
    this.renderer.setClearColor(0x87ceeb); // Sky blue
    
    // Enable shadow mapping
    // PCFShadowMap supports shadow.radius for soft/blurred edges
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // Rendered on a clock (Lighting.tickShadowUpdate), not every frame.
    this.renderer.shadowMap.autoUpdate = false;
    
    // NO tone mapping, deliberately: every lighting and sky value was tuned against the raw linear
    // result, and the post pipeline's composite does the one sRGB encode.
    this.renderer.toneMapping = THREE.NoToneMapping;

    // Add canvas to DOM
    const canvas = this.renderer.domElement;
    canvas.id = 'game-canvas';
    // Display size is governed by CSS (#game-canvas: 100%/100%); the drawing buffer
    // is set separately so renderScale can shrink it without shrinking the canvas.
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    document.body.appendChild(canvas);

    // Initialize scene, camera, lighting
    createScene();
    createCamera();
    
    // Initialize static lighting system (matches MaterialPreview)
    initLighting(this.renderer);
    
    // Apply initial environment settings from store
    const initialEnv = useGameStore.getState().environment;
    applyEnvironmentSettings(initialEnv);

    // The post pipeline (render/PostPipeline.ts): scene target, SSAO, bloom, god rays, composite.
    const scene = getScene();
    const camera = getCamera();
    this.postPipeline = initPostPipeline(this.renderer);
    if (scene) {
      // The first-person arm renders with its own ortho camera (added to the scene).
      initFirstPersonArm(scene);
    }

    setRendererRef(this.renderer);

    // Restore the last-played local world (or auto-create the first one) before
    // the terrain system starts, so it generates with the active world's seed
    // and its persisted chunks.
    await initWorlds();

    // Restore this world's persisted time-of-day (falls back to the default cycle time).
    const savedTod = loadTimeOfDay();
    if (savedTod !== null) useGameStore.getState().setTimeOfDay(savedTod);

    // Center the background orbit camera + chunk streaming on the persisted player
    // position, so the world loads AROUND where the player left off (not the origin).
    const savedPose = loadPlayerPos();
    this.hasSavedSpawn = savedPose !== null;
    if (savedPose) this.spectatorCenter.set(savedPose.x, savedPose.y, savedPose.z);

    // Initialize voxel terrain system
    if (scene) {
      this.voxelIntegration = new VoxelIntegration(scene, {
        debugEnabled: false,
      });
      
      // Set camera for visibility-based loading
      if (camera) {
        this.voxelIntegration.setCamera(camera);
      }
      
      // Note: useServerChunks is read from store by VoxelWorld
      this.voxelIntegration.init();
      // This world's coarse edit layers (player builds folded up the LOD hierarchy). Async and
      // non-blocking: a coarse chunk that arrives first is refreshed when the layers land.
      void this.voxelIntegration.world.hydrateEditLayers();
      // This world's coarse edit layers (player builds folded up the LOD hierarchy). Async and
      // non-blocking: a coarse chunk that arrives first is refreshed when the layers land.
      void this.voxelIntegration.world.hydrateEditLayers();
      
      // Wire visibility radius + far-LOD callbacks for quality system
      setVisibilityRadiusCallback((radius: number) => {
        this.voxelIntegration.world.setVisibilityRadius(radius);
      });
      setFarLodLevelsCallback((levels: number) => {
        this.voxelIntegration.world.setFarLodLevels(levels);
      });
      // Apply the quality preset (including visibility radius + far rings) and sync to store
      syncQualityToStore(qualityLevel, effectiveVisibility, effectiveFarLods);
      
      // Initialize spawn manager + the explore-mode spawn marker gizmo
      this.spawnManager = new SpawnManager(scene);
      this.spawnManager.setTerrainProvider(this.voxelIntegration);
      initSpawnMarker(scene, this.voxelIntegration);
      // A fresh world (no saved position) spawns ON the path network, same as a world switch.
      if (!savedPose) this.requestPathSpawnTarget();
      
      // Point player physics at the voxel field (not at meshes/BVH — see
      // client/src/game/voxel/physicsWorldAdapter.ts for the tri-state contract).
      const physicsField = this.playerManager.setPhysicsWorld(
        createPhysicsWorldSource(this.voxelIntegration.world)
      );
      // Terrain probes (spawn, build targeting, punch) share the SAME field instance as
      // player physics: one chunk cache, one invalidation point, and targeting that agrees
      // with collision by construction.
      this.voxelIntegration.setPhysicsField(physicsField);
      // The field caches chunk arrays, so it must be told when a chunk's ARRAY IDENTITY
      // changes. In-place voxel edits need no invalidation (that is the point of colliding
      // against live data); a fresh ingest or an unload does.
      this.voxelIntegration.world.addUnloadListener((key) => {
        const { cx, cy, cz } = parseChunkKey(key);
        physicsField.invalidateChunk(cx, cy, cz);
      });
      // Bulk clears (F9, world switch) bypass the per-chunk unload path entirely.
      this.voxelIntegration.world.onWorldCleared = () => physicsField.invalidateAll();
      
      // Set up respawn callback using SpawnManager
      this.playerManager.setRespawnFinder((currentPos, lastGrounded) => 
        this.spawnManager.findRespawnPosition(currentPos, lastGrounded)
      );
      
      // Spawn position will be calculated when entering Playing mode
      // (chunks may not be loaded yet at init time)

      // Wire tile data from VoxelWorld to map cache
      this.voxelIntegration.world.onTileReceived = (tx, tz, heights, materials) => {
        const cache = getMapTileCache();
        cache.receiveTileData(tx, tz, heights, materials);
      };

      // As real chunks stream in, refresh their map tiles so procedurally-placed
      // trees/rocks/buildings appear on the minimap (the baseline tile is stamp-free).
      this.voxelIntegration.world.onChunkIngested = (key) => {
        this.pendingMapChunks.add(key);
        const { cx, cy, cz } = parseChunkKey(key);
        physicsField.invalidateChunk(cx, cy, cz);
      };

      // Initialize build system
      this.builder.setMeshProvider(this.voxelIntegration);
      this.builder.setVoxelWorld(this.voxelIntegration.world, scene);
      this.builder.addToScene(scene);

      // Offline builds apply locally; refresh map tiles like the server-commit path does.
      this.builder.onBuildApplied = (modifiedChunks) => {
        if (modifiedChunks.length > 0) {
          this.updateMapTilesFromChunks(modifiedChunks);
        }
      };

      // Register chunk clearing callback for F9 debug
      setClearChunksCallback(() => {
        const playerPos = this.playerManager.getLocalPlayer().position.clone();
        this.voxelIntegration.clearAndReload(playerPos);
      });

      // Snap-point persistence: restore this world's points, save on change.
      const snap = this.builder.getSnapManager();
      loadSnapPoints().then((pts) => snap.setDepositedPoints(pts));
      snap.onDepositedChanged = () => saveSnapPoints(snap.getDepositedPoints());

      // Rebuild terrain + snap points when the active world changes (world picker).
      setWorldSwitchHandler(() => this.switchLocalWorld());

      // Supply the current pose so WorldManager can save the OUTGOING world on switch.
      setPlayerPosProvider(() => this.currentPlayerPose());

      // Supply the current time-of-day so it's saved per world on switch.
      setTimeOfDayProvider(() => useGameStore.getState().environment.timeOfDay);

      // Undo last build (Z, or mobile button).
      controls.onUndo = () => {
        const { keys, snapPoints } = this.voxelIntegration.world.undoLastBuild();
        if (snapPoints.length > 0) {
          snap.removeDeposited(snapPoints);
        }
        if (keys.length > 0) {
          // Mirror the place path: hand the active preview off as a commit so the reverted
          // chunk's remesh clears the stale preview mesh + restores the suppressed group.
          this.builder.commitPreview();
          this.updateMapTilesFromChunks(keys);
        }
      };
    }

    // Request pointer lock on canvas click (only when playing)
    canvas.addEventListener('click', () => {
      if (useGameStore.getState().gameMode === GameMode.Playing) {
        controls.requestPointerLock();
      }
    });

    // Register for network events
    on('snapshot', this.handleSnapshot);
    on('buildCommit', this.handleBuildCommit);
    on('chunkData', this.handleChunkData);
    on('mapTileData', this.handleMapTileData);
    on('surfaceColumnData', this.handleSurfaceColumnData);
    on('requestNack', this.handleRequestNack);

    // Handle resize
    window.addEventListener('resize', this.onResize);

    // Persist the player position when the tab is hidden/closed (mobile-safe).
    window.addEventListener('pagehide', this.savePlayerPosNow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Re-apply the drawing-buffer size whenever the render-scale slider changes.
    this.renderScaleUnsub = useGameStore.subscribe((state, prev) => {
      if (state.renderScale !== prev.renderScale) this.onResize();
    });

    // Push material/water setting changes to the shaders (replaces the old
    // bridge setters' side effects).
    this.materialSettingsUnsub = subscribeMaterialSettings();
    this.waterSettingsUnsub = subscribeWaterSettings();

    // SHADER WARM-UP (render/ShaderWarmup.ts): every define is final after syncQualityToStore, so
    // compile the manifest now. The frame loop waits only for the live scene's own programs (sky,
    // markers, arm); the terrain stays gated until the rest is linked and drawn once.
    if (scene && camera) {
      const terrain = this.voxelIntegration.world.terrain;
      terrain.setDrawGate(false);
      const warmup = startShaderWarmup(this.renderer, scene, camera, terrain);
      await warmup.sceneReady;
      void warmup.allReady;
    }

    // Start game loop and input loop
    this.gameLoop.start(this.update);
    this.playerManager.startInputLoop();
  }

  /**
   * Rebuild the local world after the active world changed (world picker).
   * Clears terrain + map tiles, regenerates with the new seed, and restores the
   * new world's snap points. Re-gates spawn so Play waits for terrain.
   */
  private switchLocalWorld(): void {
    useGameStore.getState().setSpawnReady(false);
    this.hasSpawnedPlayer = false;
    this.posSaveAccumMs = 0;
    resetMarker(); // re-auto-place the spawn marker for the new world
    getMapTileCache().clear();
    // Clear the cached surface spawn so the new world computes a FRESH one on its own terrain —
    // otherwise the previous world's spawn point can leak in until collision meshes rebuild.
    this.spawnManager?.reset();

    // Re-center the orbit camera + chunk streaming on the NEW world's saved position
    // (WorldManager.activate has already loaded it). With no saved position, center on
    // the origin — that's where SpawnManager probes for ground, so spawn resolves.
    const savedPose = loadPlayerPos();
    this.hasSavedSpawn = savedPose !== null;
    this.spawnManager?.setSpawnTarget(0, 0);
    const streamCenter = savedPose
      ? new THREE.Vector3(savedPose.x, savedPose.y, savedPose.z)
      : new THREE.Vector3(0, 0, 0);
    this.spectatorCenter.copy(streamCenter);
    // Reset the explore orbit camera onto the new world's center. Without this the module-
    // level target keeps the PREVIOUS world's location/Y (switchLocalWorld runs while in
    // explore, which never re-seeds it), so a fresh world would open framed underground.
    // The per-frame center-follow then lifts the target onto this world's surface once it
    // streams in.
    initExploreCamera(streamCenter);
    this.lastFollowX = NaN;
    this.lastFollowZ = NaN;
    // Restore the new world's persisted time-of-day (WorldManager.activate loaded it).
    const savedTod = loadTimeOfDay();
    if (savedTod !== null) useGameStore.getState().setTimeOfDay(savedTod);
    // A NEW world (no saved position) spawns ON the path network rather than at the raw origin.
    // Asked BEFORE the reload floods the queue: the pool is rebuilt on the new seed here, the
    // request is first in line on it, and the reload is told to keep that pool (and the pending
    // callback) alive. The answer then re-centres streaming after only its own latency, instead
    // of after the whole origin burst.
    if (!savedPose) {
      this.voxelIntegration.world.resetLocalPool();
      this.requestPathSpawnTarget();
    }
    this.voxelIntegration.world.reloadLocalWorld(streamCenter, /* keepPool */ !savedPose);
    // setDepositedPoints replaces the points without firing the save callback,
    // so restoring the new world's snaps doesn't overwrite them.
    const snap = this.builder.getSnapManager();
    loadSnapPoints().then((pts) => snap.setDepositedPoints(pts));
  }

  /**
   * Re-target spawn onto the path network: ask a terrain worker for the paving nearest the origin
   * and move the spawn probe, streaming centre and explore camera there. Async — until the answer
   * lands the world keeps loading around the origin, and a world with no paths stays there. The
   * pending callback dies with the worker pool on a world switch, so a stale answer cannot land;
   * a saved position or an already-spawned player wins over a late answer regardless.
   */
  private requestPathSpawnTarget(): void {
    this.voxelIntegration.world.requestPathSpawn((pt) => {
      if (!pt || this.hasSavedSpawn || this.hasSpawnedPlayer) return;
      this.spawnManager?.setSpawnTarget(pt.x, pt.z);
      const centre = new THREE.Vector3(pt.x, pt.y, pt.z);
      this.spectatorCenter.copy(centre);
      initExploreCamera(centre);
      // Force the explore centre-follow to re-place the spawn marker at the new target.
      this.lastFollowX = NaN;
      this.lastFollowZ = NaN;
    });
  }

  setLocalPlayerId(playerId: number): void {
    this.playerManager.setLocalPlayerId(playerId);
  }

  /**
   * Handle reconnection after server restart.
   * Resets game state to allow fresh start with new server.
   */
  handleReconnect(): void {
    console.log('[GameCore] Handling reconnection...');
    
    // Reset game mode to Explore so player sees the home screen
    useGameStore.getState().setGameMode(GameMode.Explore);
    
    // Reset spawn state so terrain detection starts fresh
    if (this.spawnManager) {
      this.spawnManager.reset();
    }
    useGameStore.getState().setSpawnReady(false);
    
    // Clear and reload chunks from new server
    if (this.voxelIntegration) {
      const playerPos = this.playerManager.getLocalPlayer().position.clone();
      this.voxelIntegration.clearAndReload(playerPos);
    }
    
    // Reset player spawned flag
    this.hasSpawnedPlayer = false;
    
    console.log('[GameCore] Reconnection handling complete');
  }

  private handleSnapshot = (snapshot: RoomSnapshot): void => {
    const scene = getScene();
    if (!scene) return;
    this.playerManager.handleSnapshot(snapshot, scene);
  };

  /**
   * Handle build commit from server - apply to voxel world
   */
  private handleBuildCommit = (commit: VoxelBuildCommit): void => {
    if (commit.result !== BuildResult.SUCCESS || !commit.intent) {
      return;
    }

    // Apply the build operation to voxel world (parts are the geometry)
    const operation = {
      center: commit.intent.center,
      rotation: commit.intent.rotation,
      parts: commit.intent.parts,
    };

    const modifiedChunks = this.voxelIntegration.world.applyBuildOperation(operation);

    // Nothing to rebuild for collision: physics reads the voxel field, which the edit already
    // mutated in place. That is the whole point of the field — there is no derived copy to keep
    // in step, which is what the mesh-collider era spent its bugs on.
    if (modifiedChunks.length > 0) {
      // Update map tiles for modified chunks
      this.updateMapTilesFromChunks(modifiedChunks);
    }
  };

  /** Chunk keys streamed in but not yet folded into the minimap (bounded flush). */
  private pendingMapChunks = new Set<string>();

  /** Accumulating phase for the camera head-bob while walking. */
  private headBobPhase = 0;

  /** Brief camera glide from the explore view to the first-person pose on entering Playing. */
  private cameraIntroMs = 0; // enter (explore→FP) glide remaining (ms); 0 = inactive
  private cameraIntroFromPos = new THREE.Vector3();
  private cameraIntroFromQuat = new THREE.Quaternion();
  private cameraOutroMs = 0; // exit (FP→explore) glide remaining (ms); 0 = inactive
  private cameraOutroFromPos = new THREE.Vector3();
  private cameraOutroFromQuat = new THREE.Quaternion();
  private introTmpPos = new THREE.Vector3();
  private introTmpQuat = new THREE.Quaternion();
  private static readonly CAMERA_INTRO_DURATION_MS = 1350;

  // NOTE: there used to be a `physicsHoldMs` gate here, holding gravity on entry to Playing until
  // a BVH collider existed under the player — entering play from a zoomed-out explore view releases
  // lod-0 residency, and integrating gravity over that gap dropped the player through empty space.
  // The unknown-space freeze contract replaces it exactly and without a timeout: evicted chunks read
  // UNKNOWN, and a tick whose support region is unknown does not advance the player at all.
  // Time constant (ms) for easing the explore orbit height onto the surface, so the camera
  // doesn't judder as terrain height steps between columns / streams in during a pan.
  private static readonly EXPLORE_Y_SMOOTH_MS = 140;

  /**
   * Fold a bounded batch of freshly-streamed chunks into the minimap each frame.
   * Capped so a burst of streaming can't spike a frame; the rest carry to the
   * next frame. Grouping by tile happens in updateMapTilesFromChunks.
   */
  private flushMapTilesFromStreamedChunks(): void {
    if (this.pendingMapChunks.size === 0) return;
    const MAX_PER_FRAME = 24;
    const batch: string[] = [];
    for (const key of this.pendingMapChunks) {
      batch.push(key);
      this.pendingMapChunks.delete(key);
      if (batch.length >= MAX_PER_FRAME) break;
    }
    this.updateMapTilesFromChunks(batch);
  }

  /**
   * Update map tiles from modified chunks.
   * Uses the shared updateTileFromChunk function.
   */
  private updateMapTilesFromChunks(chunkKeys: string[]): void {
    const cache = getMapTileCache();
    const world = this.voxelIntegration.world;

    // Group chunks by tile (tx, tz) since multiple Y-level chunks affect same tile
    const tileUpdates = new Map<string, string[]>();
    
    for (const key of chunkKeys) {
      const chunk = world.chunks.get(key);
      if (!chunk) continue;
      
      const tileKey = mapTileKey(chunk.cx, chunk.cz);
      if (!tileUpdates.has(tileKey)) {
        tileUpdates.set(tileKey, []);
      }
      tileUpdates.get(tileKey)!.push(key);
    }

    // Update each affected tile
    for (const [tileKey, chunkKeysForTile] of tileUpdates) {
      const { tx, tz } = parseMapTileKey(tileKey);
      
      // Get or create tile in cache
      let tile = cache.get(tx, tz);
      if (!tile) {
        tile = createMapTile(tx, tz);
        cache.set(tx, tz, tile);
      }
      
      // Update tile from each chunk in this column
      for (const chunkKey of chunkKeysForTile) {
        const chunk = world.chunks.get(chunkKey);
        if (chunk) {
          updateTileFromChunk(tile, chunk);
        }
      }

      // Recompute hash after all chunk updates for this tile
      updateTileHash(tile);

      // The edited surface is now the truth for the minimap and the lod-0 sky clip (in-memory
      // only — a reload re-derives both from generation plus the persisted edited chunks).
      this.voxelIntegration.world.updateColumnFromEditedTile(tx, tz, tile.heights);
    }
  }

  /**
   * Handle chunk data from server - apply to voxel world
   */
  private handleChunkData = (chunkData: VoxelChunkData): void => {
    if (!this.voxelIntegration) return;
    
    // Apply chunk data to voxel world
    this.voxelIntegration.world.receiveChunkData(chunkData);
  };

  /**
   * Handle map tile data from server - route through VoxelWorld for column tracking
   */
  private handleMapTileData = (tileData: MapTileResponse): void => {
    if (!this.voxelIntegration) return;
    this.voxelIntegration.world.receiveTileData(tileData);
  };

  /**
   * Handle surface column data from server - contains tile + chunks
   */
  private handleSurfaceColumnData = (columnData: SurfaceColumnResponse): void => {
    if (!this.voxelIntegration) return;
    this.voxelIntegration.world.receiveSurfaceColumnData(columnData);
  };

  /**
   * Handle request NACK from server - clear pending entry so it can be re-requested
   */
  private handleRequestNack = (nack: RequestNack): void => {
    if (!this.voxelIntegration) return;
    this.voxelIntegration.world.handleRequestNack(nack);
  };

  /**
   * Main update loop callback - called by GameLoop
   */
  private update = (deltaMs: number, elapsedTime: number): void => {
    perfStats.begin('gameUpdate');
    perfStats.setFrameInterval(deltaMs); // the frame budget's clock — see voxel/frameBudget.ts

    const gameMode = useGameStore.getState().gameMode;
    const camera = getCamera();
    const localPlayer = this.playerManager.getLocalPlayer();

    // Check for game mode transitions
    this.handleGameModeTransition(gameMode, localPlayer);

    // Sync voxel debug state from store to VoxelDebugManager
    this.updateVoxelDebug();

    // Update based on current game mode
    switch (gameMode) {
      case GameMode.Explore:
        this.updateExploreMode(camera, deltaMs);
        break;

      case GameMode.MainMenu:
      case GameMode.Spectating:
        this.updateSpectatorMode(camera, deltaMs, elapsedTime);
        break;

      case GameMode.Playing:
        this.updatePlayingMode(camera, localPlayer, deltaMs);
        break;
    }

    // Explore's off-centre framing (the panel reserves the left of the screen). Driven in every
    // mode, not just Explore: that is what eases it back to centre under the play camera glide.
    if (camera) advanceExploreViewShift(camera, deltaMs, gameMode === GameMode.Explore);

    // Outside Playing, drive the arm's slide-down exit (or keep it hidden). updatePlayingMode
    // drives it while playing.
    if (gameMode !== GameMode.Playing) tickFirstPersonArmExit(deltaMs);

    // Refresh minimap tiles from freshly-streamed chunks (bounded per frame).
    this.flushMapTilesFromStreamedChunks();

    // Always update remote players (visible in all modes)
    perfStats.begin('players');
    this.playerManager.updateRemotePlayers(deltaMs);
    perfStats.end('players');

    // Update map overlay positions (all modes so spectator map shows players)
    const mapCenter = gameMode === GameMode.Playing ? localPlayer.position : this.spectatorCenter;
    updateMapPlayerPosition(mapCenter.x, mapCenter.z, localPlayer.yaw, this.playerManager.getLocalPlayerColor());
    updateMapOtherPlayers(this.playerManager.getRemotePlayerPositions());

    // Bound the map-tile cache: when the player crosses into a new tile, evict tiles far from them so
    // exploring a large world doesn't grow the (otherwise never-evicted) cache without bound.
    const tileMeters = CHUNK_SIZE * VOXEL_SCALE;
    const ctx = Math.floor(mapCenter.x / tileMeters), ctz = Math.floor(mapCenter.z / tileMeters);
    if (ctx !== this.mapPruneTx || ctz !== this.mapPruneTz) {
      this.mapPruneTx = ctx; this.mapPruneTz = ctz;
      getMapTileCache().prune(ctx, ctz, MAP_TILE_KEEP_RADIUS);
    }

    // Update wind animation for foliage
    updateWindTime(elapsedTime);

    // Update day-night cycle + sky. updateDayNightCycle applies the derived
    // sun/moon/hemisphere values directly to the lights + sky each frame — the
    // store is no longer round-tripped per frame (manual env edits apply
    // themselves on-change via the debug panel / subscriptions).
    perfStats.begin('environment');
    updateDayNightCycle(deltaMs);
    updateSkyTime(elapsedTime);
    if (camera) {
      updateSkyCamera(camera);
    }
    perfStats.end('environment');

    // Update shadow camera to follow current center point
    const shadowCenter = gameMode === GameMode.Playing ? localPlayer.position : this.spectatorCenter;
    // ...and to MATCH THE ZOOM. Play mode pins base 0, so this is a no-op there; explore scales the
    // whole shadow volume by 2^displayBaseLod so shadow resolution per screen pixel stays roughly
    // constant as the view widens (see Lighting.shadowLodScale).
    if (this.voxelIntegration) setShadowLodScale(this.voxelIntegration.world.currentBaseLod);
    updateShadowFollow(shadowCenter);
    tickShadowUpdate(this.renderer, performance.now());

    // Late-latch camera rotation: apply the very latest mouse input just before
    // rendering, so any mousemove events that arrived during the update phase
    // (physics, environment, shadows, etc.) are reflected this frame rather than
    // being deferred to the next one.
    // (Suppressed during the intro glide, which owns the camera orientation.)
    if (gameMode === GameMode.Playing && camera && this.cameraIntroMs === 0) {
      camera.rotation.set(controls.pitch, controls.yaw, 0);
    }

    // Render through effects pipeline
    const scene = getScene();
    if (scene && camera) {
      perfStats.begin('render');
      // renderer.info resets on EVERY render() call by default, so it read the last pass alone
      // ("Calls: 1"). Reset once per frame and let every pass accumulate.
      this.renderer.info.autoReset = false;
      this.renderer.info.reset();
      this.gpuTimer?.begin();
      this.postPipeline.render(scene, camera);
      // Draw the first-person arm on top (own ortho camera + layers, cleared depth)
      // so it's never occluded by water/geometry but still lit by the scene lights.
      renderFirstPersonArm(this.renderer, scene);
      this.gpuTimer?.end();
      perfStats.end('render');
      if (this.gpuTimer) {
        this.gpuTimer.poll();
        perfStats.setGpuMs(this.gpuTimer.ms);
      }
      perfStats.captureRendererInfo(this.renderer);
      assertNoColdCompiles(this.renderer);
    }

    perfStats.end('gameUpdate');
    perfStats.endFrame();
  };

  /**
   * Handle game mode transitions (e.g. MainMenu → Playing)
   */
  private handleGameModeTransition(currentMode: GameMode, localPlayer: ReturnType<PlayerManager['getLocalPlayer']>): void {
    if (currentMode === this.lastGameMode) return;
    
    const previousMode = this.lastGameMode;
    this.lastGameMode = currentMode;
    
    // Leaving Playing mode - capture player position for spectator center + persist it
    if (previousMode === GameMode.Playing) {
      this.spectatorCenter.copy(localPlayer.position);
      this.savePlayerPosNow();
    }

    // Entering Explore (boot or pause) - seed the free camera on the current center and
    // re-place the spawn marker there (updateExploreMode auto-places once terrain is
    // ready), so pausing drops the marker where you are and tapping Play resumes.
    if (currentMode === GameMode.Explore) {
      // Smooth camera-out: glide from the current first-person pose to the explore view.
      if (previousMode === GameMode.Playing) {
        const cam = getCamera();
        if (cam) {
          this.cameraOutroFromPos.copy(cam.position);
          this.cameraOutroFromQuat.copy(cam.quaternion);
          this.cameraOutroMs = GameCore.CAMERA_INTRO_DURATION_MS;
          this.cameraIntroMs = 0; // cancel any in-flight enter glide
        }
      }
      initExploreCamera(this.spectatorCenter);
      resetMarker();
      // Leaving play — slide the arm out and re-arm the reveal (hotbar + pills slide out via
      // firstPersonReady flipping false). The explore UI slides back in only once the outro glide
      // completes (exploreReady set below); a non-play entry (boot) has no outro, so show it now.
      if (previousMode === GameMode.Playing) startFirstPersonArmExit();
      else useGameStore.getState().setExploreReady(true);
      useGameStore.getState().setFirstPersonReady(false);
    } else {
      setMarkerVisible(false); // hide the spawn gizmo outside explore
    }

    // NO BUILD VISUALS OUTSIDE PLAY, and no build mode carried into explore. The builder only
    // updates from updatePlayingMode, so a preview box, target marker or snap dot that was on
    // screen at the moment the mode changed had nothing left to clear it and hung over the explore
    // camera. Dropping build mode too means tapping Play comes back in walk mode, and the hotbar
    // agrees with what the world shows.
    if (currentMode !== GameMode.Playing) {
      this.builder.suspendVisuals();
      if (useGameStore.getState().build.buildMode) useGameStore.getState().setBuildMode(false);
    }

    // Entering Playing mode - spawn at the chosen marker if the Play button armed one
    // (works even for a re-play after pausing); otherwise the first-time spawn logic.
    if (currentMode === GameMode.Playing) {
      // First person pins base LOD 0 — refinement cascades during the camera intro glide.
      resetLodZoom();
      this.voxelIntegration?.world.setTargetBaseLod(0);
      const cam0 = getCamera();
      this.updateCameraLodRange(cam0 ?? null, this.voxelIntegration?.world ?? null);
      useGameStore.getState().setExploreReady(false); // explore UI animates out
      const markerSpawn = consumeMarkerSpawn();
      if (markerSpawn && !this.voxelIntegration?.world.isSpawnReadyAt(markerSpawn.x, markerSpawn.y, markerSpawn.z)) {
        // The button is disabled until ready, so this is the race/keyboard path. DEFER rather than
        // place: spawning now means freezing in mid-air over coarse ground (the whole point of the
        // gate). updatePlayingMode retries each frame and spawns the moment lod-0 is there.
        this.pendingMarkerSpawn = markerSpawn;
      } else if (markerSpawn) {
        // RE-RESOLVE Y AGAINST THE VOXEL FIELD. The marker is placed by a RENDER raycast, which
        // hits whatever is drawn at explore zoom — including coarse far-LOD rings, whose decimated
        // surface can sit tens of metres off the lod-0 surface physics actually collides with.
        // Spawning at the render Y drops the player inside rock (wedged → stuck → respawn) or into
        // a fall from height. The marker's XZ is exactly what the user chose, so keep it and take Y
        // from the field; if that column isn't resident yet, keep the marker's Y and let the
        // unknown-space freeze hold the player until it streams.
        const grounded = this.spawnManager?.resolveGroundSpawn(markerSpawn.x, markerSpawn.z);
        this.playerManager.setSpawnPosition(grounded ?? markerSpawn);
        controls.pitch = 0; // level look on a fresh marker spawn
        this.hasSpawnedPlayer = true;
        this.spawnManager?.clearDebugVisualization();
      } else if (!this.hasSpawnedPlayer) {
        this.spawnPlayer();
      }

      // Start a brief camera glide from the explore view to the first-person pose, so
      // entering play reads as a smooth move rather than a hard cut. Capture the current
      // (explore) camera pose as the start; the end is tracked live from the player.
      if (previousMode === GameMode.Explore) {
        const cam = getCamera();
        if (cam) {
          this.cameraIntroFromPos.copy(cam.position);
          this.cameraIntroFromQuat.copy(cam.quaternion);
          this.cameraIntroMs = GameCore.CAMERA_INTRO_DURATION_MS;
        }
      }
    }
  }

  /**
   * Calculate spawn position and place player.
   * Called when entering Playing mode for the first time.
   */
  private spawnPlayer(): void {
    if (!this.spawnManager) return;

    // Prefer the world's persisted position; fall back to terrain-raycast spawn for a
    // fresh world. Restored exactly as saved: the physics engine freezes until the chunk
    // under the player is resident, so there is nothing to clip into and no need for the
    // old "lift it a bit and hope" offset.
    const saved = loadPlayerPos();
    if (saved) {
      this.playerManager.setSpawnPosition(new THREE.Vector3(saved.x, saved.y, saved.z));
      controls.yaw = saved.yaw;
      controls.pitch = saved.pitch;
    } else {
      this.playerManager.setSpawnPosition(this.spawnManager.getCachedSpawnPosition());
    }
    this.hasSpawnedPlayer = true;

    // Clear spawn debug visualization now that game is starting
    this.spawnManager.clearDebugVisualization();
  }

  /** Current player pose (position + look) for persistence. */
  private currentPlayerPose(): { x: number; y: number; z: number; yaw: number; pitch: number } {
    const p = this.playerManager.getLocalPlayer().position;
    return { x: p.x, y: p.y, z: p.z, yaw: controls.yaw, pitch: controls.pitch };
  }

  /** Save the player's position now, if they've spawned (position is meaningful). */
  private savePlayerPosNow = (): void => {
    if (this.hasSpawnedPlayer) savePlayerPos(this.currentPlayerPose());
    // Time-of-day advances in every mode, so persist it regardless of spawn state.
    saveTimeOfDay(useGameStore.getState().environment.timeOfDay);
  };

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') this.savePlayerPosNow();
  };

  // ---- Per-frame field probes (see updateFieldProbes) ----
  /** Scratch, reused every frame — these run per tick and must not allocate. */
  private readonly probeRayHit: RaycastHit =
    { x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 0, t: 0, material: 0, gx: 0, gy: 0, gz: 0 };
  private readonly probeSubmersion: SubmersionResult =
    { fraction: 0, eyeInLiquid: false, feetInLiquid: false, liquidMaterial: -1 };
  private probeGroundMaterial = -1;
  private probeTargetMaterial = -1;

  /**
   * Sample the voxel field around the player: what is underfoot, how deep the capsule is in liquid,
   * and what a punch would hit. Read-only — the solver is untouched.
   *
   * `eyePos` IS PlayerLocal.position (see its doc), which is what sampleSubmersion wants. The punch
   * ray deliberately mirrors Builder.handlePunch exactly (same origin, mask and MAX_BUILD_DISTANCE),
   * so the readout can never disagree with what a click would actually dig.
   */
  private updateFieldProbes(camera: THREE.Camera | null, eyePos: THREE.Vector3): void {
    const vox = this.voxelIntegration;
    if (!vox) return;

    const feetY = eyePos.y - PLAYER_HEIGHT_INNER - PLAYER_RADIUS;
    // A voxel of slack above the feet: standing exactly on a boundary otherwise starts the cast
    // inside the block it is trying to name.
    const ground = vox.probeDown(eyePos.x, eyePos.z, feetY + VOXEL_SCALE, 2 * VOXEL_SCALE, true);
    this.probeGroundMaterial = ground ? ground.material : -1;

    if (!vox.probeSubmersion(eyePos.x, eyePos.y, eyePos.z, this.probeSubmersion)) {
      this.probeSubmersion.fraction = 0;
      this.probeSubmersion.feetInLiquid = false;
      this.probeSubmersion.eyeInLiquid = false;
      this.probeSubmersion.liquidMaterial = -1;
    }

    this.probeTargetMaterial = -1;
    if (camera) {
      _probeDir.set(0, 0, -1).applyQuaternion(camera.quaternion);
      const hit = vox.probeRay(
        camera.position.x, camera.position.y, camera.position.z,
        _probeDir.x, _probeDir.y, _probeDir.z,
        MAX_BUILD_DISTANCE, RAY_SOLID | RAY_LIQUID | RAY_TRANSPARENT, this.probeRayHit,
      );
      if (hit) this.probeTargetMaterial = this.probeRayHit.material;
    }
  }

  /**
   * Sync voxel debug state from store to VoxelDebugManager
   */
  private updateVoxelDebug(): void {
    if (!this.voxelIntegration) return;

    const debugState = useGameStore.getState().voxelDebug;
    this.voxelIntegration.debug.setState(debugState);

    // Sync wireframe mode to shared material
    setVoxelWireframe(debugState.showWireframe);

    // F5: skirts AND their masks. Off restores the masked bands, which is what makes it an A/B of the
    // whole mechanism rather than of "no new skirts".
    const stitcher = this.voxelIntegration.world.seamStitcher;
    stitcher.setSkirtsEnabled(debugState.seamSkirts ?? true);
    // F6: the ring/rim overlay. setSeamDebug forces a rebuild when it turns on, because a settled
    // world flushes nothing and the overlay would come up empty.
    stitcher.setSeamDebug(debugState.seamDebug ?? false);
    this.voxelIntegration.debug.updateSeamDebug(debugState.seamDebug ?? false, stitcher.seamDebug);

    // ...AND ONLY WHILE SOMETHING IS READING THEM. Everything below exists for the debug panel, and
    // `getLodStatusSummary` walks every geometry with a display probe each (twice a second, on its own
    // 500 ms cache) to produce it. With the panel collapsed nobody sees any of it.
    if (!useGameStore.getState().debugPanelExpanded) return;

    // Update voxel stats in store — but only when a value actually changed. This
    // write happens every frame; a bare `setVoxelStats` allocates a new object each
    // time and re-renders every store subscriber, so gate it on change.
    const stats = this.voxelIntegration.getStats();
    const debugObjects = this.voxelIntegration.debug.getDebugObjectCount();
    const world = this.voxelIntegration.world;
    const baseLod = world.currentBaseLod;
    // getLodStatusSummary FIRST: getDrawnTotal and getWasteSummary both report what THAT call
    // counted (one walk over every geometry, one 500 ms cache) rather than walking it again.
    const lodStatus = world.getLodStatusSummary();
    const chunksDrawn = world.getDrawnTotal();
    const meshChurn = world.getMeshChurnSummary();
    const wastePct = world.getWasteSummary();
    const skirtKinds = seamKindSummary();
    const last = this.lastVoxelStats;
    if (
      stats.chunksLoaded !== last.chunksLoaded ||
      stats.meshesVisible !== last.meshesVisible ||
      debugObjects !== last.debugObjects ||
      baseLod !== last.baseLod ||
      chunksDrawn !== last.chunksDrawn ||
      lodStatus !== last.lodStatus
      || meshChurn !== last.meshChurn
      || wastePct !== last.wastePct
      || skirtKinds !== last.skirtKinds
    ) {
      last.chunksLoaded = stats.chunksLoaded;
      last.meshesVisible = stats.meshesVisible;
      last.debugObjects = debugObjects;
      last.baseLod = baseLod;
      last.chunksDrawn = chunksDrawn;
      last.lodStatus = lodStatus;
      last.meshChurn = meshChurn;
      last.wastePct = wastePct;
      last.skirtKinds = skirtKinds;
      useGameStore.getState().setVoxelStats({
        chunksLoaded: stats.chunksLoaded,
        meshesVisible: stats.meshesVisible,
        debugObjects,
        baseLod,
        chunksDrawn,
        lodStatus,
        meshChurn,
        wastePct,
        skirtKinds,
      });
    }
  }

  /**
   * Update for Explore mode — user-driven free 3rd-person camera over the world.
   */
  private updateExploreMode(camera: THREE.PerspectiveCamera | null, deltaMs: number): void {
    // WASD/arrows slide the orbit target across the ground — center-follow below then re-pins
    // the spawn marker to the surface under it, so held keys walk the spawn across the terrain.
    // Skipped while the user drags the marker, or is typing in a field (world name, etc.).
    if (!isExploreMarkerInteracting() && !isTypingTarget(document.activeElement)) {
      const { moveX, moveZ } = controls.getMoveVector();
      if (moveX !== 0 || moveZ !== 0) exploreCameraMove(moveX, moveZ, deltaMs);
    }
    // Advance any in-progress recenter glide (eases the orbit target toward a moved spawn),
    // then run "center-follow": pin the spawn marker to the surface directly under screen
    // center (the orbit target) and sit the target on that surface, so the spawn stays
    // centered as the user pans. Suspended while the user grabs the marker or a glide is
    // animating (so those interactions aren't overridden). Retries each frame until the
    // center column's terrain has streamed — which also lifts a fresh world onto its surface.
    advanceExploreTargetGlide(deltaMs);
    advanceExploreZoomTween(deltaMs);
    setMarkerVisible(true);
    const target = getExploreTarget();
    if (!isExploreMarkerInteracting() && !isExploreGliding()) {
      const wasPlaced = isMarkerPlaced();
      const moved = target.x !== this.lastFollowX || target.z !== this.lastFollowZ;
      if ((moved || !wasPlaced) && placeMarkerAtColumn(target.x, target.z)) {
        this.lastFollowX = target.x;
        this.lastFollowZ = target.z;
      }
      // SEED THE CENTRE FROM THE TILE, before any mesh exists. The marker needs drawn geometry to
      // raycast against, so on a fresh world it cannot place until chunks have meshed — and until it
      // does, the centre (which IS the streaming centre) sits at its initial y. The tile carries the
      // column's surface height with no chunk resident at all, so this lifts the centre onto the
      // surface for the very first streaming pass instead of after the first fill.
      if (!isMarkerPlaced() && this.voxelIntegration) {
        const h = this.voxelIntegration.world.surfaceHeightAt(target.x, target.z);
        if (h !== null && Math.abs(target.y - h) > 0.5) target.y = h;
      }
      if (isMarkerPlaced()) {
        if (!wasPlaced) {
          // FIRST PLACEMENT (a world just opened, or resetMarker on a world switch): SNAP.
          //
          // The orbit target is also the STREAMING CENTRE, and on a fresh world it starts at
          // y = 0 while the surface can be hundreds of metres up (landform terrain spans
          // −141 m … 915 m). Easing across that gap drags the streaming centre through dozens of
          // chunk rows: every 8 m is a chunk crossing, and every crossing re-boxes the visibility
          // BFS, recomputes every LOD shell, and evicts what the centre just left — a whole
          // initial fill generated and thrown away, and (since no-steal) a hole window at each
          // freshly-entered octet. Nothing is on screen yet at first placement, so there is no
          // judder to smooth: the ease was buying nothing and costing the entire cold start.
          target.y = getMarkerBase().y;
        } else {
          // Afterwards, ease: the surface height changes continuously across a pan (and as
          // chunks stream in), and there the smoothing is exactly right — it keeps the camera
          // from juddering, and a pan moves the centre gradually anyway.
          const k = 1 - Math.exp(-deltaMs / GameCore.EXPLORE_Y_SMOOTH_MS);
          target.y += (getMarkerBase().y - target.y) * k;
        }
      }
    }

    if (camera) updateExploreCamera(camera);

    // Brief first-person→explore glide on exiting play: blend from the captured FP pose
    // toward the explore pose (which updateExploreCamera just wrote into the camera).
    if (camera && this.cameraOutroMs > 0) {
      this.cameraOutroMs = Math.max(0, this.cameraOutroMs - deltaMs);
      const t = 1 - this.cameraOutroMs / GameCore.CAMERA_INTRO_DURATION_MS;
      const eased = easeInOut(t);
      this.introTmpPos.copy(camera.position);
      this.introTmpQuat.copy(camera.quaternion);
      camera.position.copy(this.cameraOutroFromPos).lerp(this.introTmpPos, eased);
      camera.quaternion.copy(this.cameraOutroFromQuat).slerp(this.introTmpQuat, eased);
      // Outro finished → let the explore UI (world/settings/panel) animate back in.
      if (this.cameraOutroMs === 0) useGameStore.getState().setExploreReady(true);
    }

    // SPAWN GATE. Readiness is a property of WHERE THE MARKER IS, and it has to be computed here:
    // it used to be produced only by the spectator-mode path, which explore never enters, so the
    // store's flag sat at its initial value and nothing could gate on it. Coarse terrain is drawn
    // wherever lod-0 has not arrived, so without this the Play button is live over ground the
    // physics engine knows nothing about (see VoxelWorld.isSpawnReadyAt).
    if (this.voxelIntegration) {
      const spawn = getMarkerSpawnPosition();
      const ready = isMarkerPlaced() && this.voxelIntegration.world.isSpawnReadyAt(spawn.x, spawn.y, spawn.z);
      if (ready !== useGameStore.getState().spawnReady) useGameStore.getState().setSpawnReady(ready);
      // SEPARATE FROM THE SPAWN GATE: the chunk is "loaded + meshed + sky provable", but the user
      // sees it as a hole until the walk shows it (or a preview does). The Play
      // button waits on the audit-truth `isChunkDisplayed`, not the meshing truth, so we never
      // advertise Play over a chunk the user can't actually see.
      // The GROUND of the marker's column, not the marker's eye position — see isGroundDisplayedAt
      // for why the eye position answered about empty sky ~45% of the time.
      const markerBase = getMarkerBase();
      const lod0Visible = isMarkerPlaced()
        && this.voxelIntegration.isGroundDisplayedAt(markerBase.x, markerBase.z, markerBase.y);
      if (lod0Visible !== useGameStore.getState().spawnLod0Visible) {
        useGameStore.getState().setSpawnLod0Visible(lod0Visible);
      }
    }

    // The explore target is the world stream/shadow center; keep spectatorCenter in sync
    // (with the surface-corrected Y from center-follow above) so streaming, shadows, the
    // map, and the visibility BFS follow where the user pans/spawns.
    // A GLIDE STREAMS AT ITS DESTINATION. The camera eases over up to 1.2 s at ~400 m/s, and the
    // centre riding along crossed a chunk nearly every frame — each one re-boxing the BFS and
    // evicting what it had just fetched. The far tap is a jump: the centre goes there at once (the
    // cold-load path), so the destination is filling while the camera is still on its way.
    this.spectatorCenter.copy(isExploreGliding() ? getExploreGlideDestination() : target);

    if (this.voxelIntegration) {
      // Zoom → base LOD: the smoothed/hysteretic camera zoom level sets the TARGET base LOD; the
      // world steps its displayed base level toward it one LOD at a time as coverage permits.
      const world = this.voxelIntegration.world;
      const maxBaseLod = world.maxBaseLod;
      setExploreMaxBaseLod(maxBaseLod);
      world.setTargetBaseLod(updateLodZoom(deltaMs, getExploreZoomLevel(), maxBaseLod));
      this.updateCameraLodRange(camera, world);

      perfStats.begin('voxelUpdate');
      this.voxelIntegration.update(this.spectatorCenter);
      perfStats.end('voxelUpdate');
    }
  }

  /** Cube corner / face distance: the deepest shell's corner is √3 further out than its face. */
  private static readonly SHELL_DIAGONAL = Math.sqrt(3);

  /**
   * Keep the camera far plane clear of the LOD onion's actual reach, so the deepest rings are not
   * generated and then clipped away unseen. Derived from the onion itself (base radius, displayed base
   * lod, ring count) rather than a multiple of a literal: the old version scaled 1000 m by the base lod
   * and ignored `farLodLevels` entirely, so at 8 rings everything past 1 km was invisible — and it was
   * only called from the explore path, so first person never scaled at all.
   *
   * Never shrinks below the classic 1 km default, and only touches the projection matrix on a change.
   */
  private updateCameraLodRange(camera: THREE.PerspectiveCamera | null, world: VoxelWorld | null): void {
    if (!camera || !world) return;
    const maxLod = world.currentBaseLod + world.farLodLevels;
    const reach = lodViewReachMetres(world.visibilityRadius, maxLod);
    // ×√3 for the box diagonal (the corner of the deepest shell is further than its face).
    const far = Math.max(CAMERA_FAR_DEFAULT, Math.ceil(reach * GameCore.SHELL_DIAGONAL));
    // THE NEAR PLANE IS THE DEPTH PRECISION LEVER (no logarithmic depth). z/w precision at distance d
    // is ~d²/(near·2²⁴), so every doubling of `near` halves the z-fight band at the far rings. First
    // person keeps CAMERA_NEAR (the arm renders with its own camera, but the build marker is close).
    // Explore orbits from metres to hundreds of metres away with nothing near the lens, so its near
    // plane follows the orbit distance — at a 35 km far plane zoomed out, that is the difference
    // between a legible horizon and a shimmering one.
    const explore = useGameStore.getState().gameMode === GameMode.Explore;
    const near = explore
      ? Math.min(GameCore.EXPLORE_NEAR_MAX, Math.max(CAMERA_NEAR, getExploreDistance() * GameCore.EXPLORE_NEAR_RATIO))
      : CAMERA_NEAR;
    if (camera.far !== far || camera.near !== near) {
      camera.far = far;
      camera.near = near;
      camera.updateProjectionMatrix();
    }
  }

  private static readonly EXPLORE_NEAR_RATIO = 0.02;
  private static readonly EXPLORE_NEAR_MAX = 8;

  /**
   * Update for MainMenu/Spectating modes - orbiting camera
   */
  private updateSpectatorMode(
    camera: THREE.PerspectiveCamera | null,
    deltaMs: number,
    elapsedTime: number
  ): void {
    if (camera) {
      updateSpectatorCamera(camera, deltaMs, elapsedTime, this.spectatorCenter);
    }

    // Update voxel terrain centered on spectator center (preserves chunks when returning from playing)
    if (this.voxelIntegration) {
      perfStats.begin('voxelUpdate');
      this.voxelIntegration.update(this.spectatorCenter);
      perfStats.end('voxelUpdate');
    }
    
    // Update spawn detection (only needed before first spawn; after that, player
    // respawns at their last position so terrain raycast is unnecessary)
    if (this.spawnManager && !this.hasSpawnedPlayer) {
      if (this.hasSavedSpawn) {
        // A persisted position is an absolute spawn — ready without a terrain raycast
        // (which probes the origin and would never resolve for a far-away saved world).
        if (!useGameStore.getState().spawnReady) useGameStore.getState().setSpawnReady(true);
      } else {
        this.spawnManager.update();
        // Update spawn ready state in store
        const spawnReady = this.spawnManager.isSpawnReady();
        if (spawnReady !== useGameStore.getState().spawnReady) {
          useGameStore.getState().setSpawnReady(spawnReady);
        }
      }
    } else if (this.hasSpawnedPlayer && !useGameStore.getState().spawnReady) {
      // Already spawned before — always ready to respawn
      useGameStore.getState().setSpawnReady(true);
    }
  }

  /**
   * Push the frozen state to the store, debounced, and only on transitions (per-frame store
   * writes would re-render the React tree).
   */
  private updateFrozenFlag(frozen: boolean, deltaMs: number): void {
    this.frozenMs = frozen ? this.frozenMs + deltaMs : 0;
    const shouldShow = this.frozenMs > GameCore.FROZEN_INDICATOR_DELAY_MS;
    if (shouldShow !== this.frozenFlag) {
      this.frozenFlag = shouldShow;
      useGameStore.getState().setPhysicsFrozen(shouldShow);
    }
  }

  /**
   * Update for Playing mode - FPS controls
   */
  private updatePlayingMode(
    camera: THREE.PerspectiveCamera | null,
    localPlayer: ReturnType<PlayerManager['getLocalPlayer']>,
    deltaMs: number
  ): void {
    // A spawn that outran its terrain: retry until lod-0 is actually there, then place. Until it
    // lands the player stays wherever they were, which is the same "do not guess" contract the
    // respawn path uses — better than dropping them into a world the physics engine cannot see.
    if (this.pendingMarkerSpawn && this.voxelIntegration) {
      const p = this.pendingMarkerSpawn;
      if (this.voxelIntegration.world.isSpawnReadyAt(p.x, p.y, p.z)) {
        this.playerManager.setSpawnPosition(p);
        controls.pitch = 0;
        this.hasSpawnedPlayer = true;
        this.pendingMarkerSpawn = null;
        this.spawnManager?.clearDebugVisualization();
      }
    }

    // The build menu soft-pauses play: freeze the player + build preview while it's
    // open (it takes over the whole window), but keep streaming + rendering so the
    // world stays live behind the translucent overlay and thumbnails keep rendering.
    const menuPaused = useGameStore.getState().build.menuOpen;

    // Stream chunks BEFORE stepping physics. Physics reads voxel data directly, so a chunk
    // that arrives this frame can be stood on this frame instead of next — and a player
    // frozen at the streaming frontier unfreezes as soon as the data lands. (The old order
    // was physics-then-sync, which is one of the reasons the mesh collider path let people
    // fall through ground that had technically already arrived.)
    if (this.voxelIntegration) {
      perfStats.begin('voxelUpdate');
      this.voxelIntegration.update(localPlayer.position);
      perfStats.end('voxelUpdate');
    }

    // Update player physics and movement
    perfStats.begin('physics');
    if (!menuPaused) this.playerManager.updateLocalPlayer(deltaMs);
    // Field probes run INSIDE the physics window so their cost is attributed to Phys instead of
    // landing in no term at all. They are queries against the field the solver just collided
    // against, which is what makes them usable by the swimming work later.
    this.updateFieldProbes(camera, localPlayer.position);
    perfStats.end('physics');

    // Surface physics state for the debug panel, and (change-gated) the "waiting for
    // terrain" pill — a frozen player is standing still on purpose, and that must be
    // visible rather than looking like a hang.
    const step = localPlayer.getStepResult();
    updatePhysicsHud({
      frozen: step.frozen,
      blockedByUnknown: step.blockedByUnknown,
      grounded: step.grounded,
      stuck: step.stuck,
      steppedUp: step.steppedUp,
      hitCeiling: step.hitCeiling,
      wallContact: step.wallContact,
      depenetrations: step.depenetrations,
      fallTime: localPlayer.getFallTime(),
      velocityY: localPlayer.velocity.y,
      groundMaterial: this.probeGroundMaterial,
      targetMaterial: this.probeTargetMaterial,
      submersionFraction: this.probeSubmersion.fraction,
      feetInLiquid: this.probeSubmersion.feetInLiquid,
      eyeInLiquid: this.probeSubmersion.eyeInLiquid,
      liquidMaterial: this.probeSubmersion.liquidMaterial,
    });
    this.updateFrozenFlag(step.frozen, deltaMs);

    // Update camera and build system
    if (camera) {
      updateCameraFromPlayer(camera, localPlayer);
      // First person pins base LOD 0 but the RING COUNT is a live setting, so the far plane is
      // re-derived here too — it early-outs unless the value actually changes. Without this, changing
      // Far View mid-play left the new rings outside the frustum (and the old code never scaled the
      // far plane in first person at all).
      this.updateCameraLodRange(camera, this.voxelIntegration?.world ?? null);
      // Brief explore→first-person glide: blend from the captured explore pose toward
      // the live FP pose (which updateCameraFromPlayer just wrote into the camera).
      if (this.cameraIntroMs > 0) {
        this.cameraIntroMs = Math.max(0, this.cameraIntroMs - deltaMs);
        const t = 1 - this.cameraIntroMs / GameCore.CAMERA_INTRO_DURATION_MS;
        const eased = easeInOut(t);
        this.introTmpPos.copy(camera.position);
        this.introTmpQuat.copy(camera.quaternion);
        camera.position.copy(this.cameraIntroFromPos).lerp(this.introTmpPos, eased);
        camera.quaternion.copy(this.cameraIntroFromQuat).slerp(this.introTmpQuat, eased);
      }
      if (!menuPaused) {
        perfStats.begin('buildPreview');
        this.builder.update(camera, localPlayer.position);
        perfStats.end('buildPreview');
      }
    }

    // Camera head-bob while moving — a subtle vertical bob applied to the camera
    // position (rotation is late-latched at render, so only position is safe here).
    // The first-person arm is a camera child, so it inherits this motion.
    const move = controls.getMoveVector();
    const speed = Math.hypot(move.moveX, move.moveZ);
    let headBob = 0;
    // Skip head-bob during the intro glide so it doesn't fight the tween.
    if (camera && !menuPaused && this.cameraIntroMs === 0) {
      if (speed > 0.1) this.headBobPhase += (Math.min(deltaMs, 100) / 1000) * 9;
      headBob = Math.sin(this.headBobPhase * 2) * 0.05 * Math.min(1, speed);
      camera.position.y += headBob;
    }

    // Once the explore→FP camera glide finishes, flag first-person ready so the arm + hotbar
    // reveal together (covers no-glide re-entry too, where cameraIntroMs is already 0).
    if (this.cameraIntroMs === 0 && !useGameStore.getState().firstPersonReady) {
      useGameStore.getState().setFirstPersonReady(true);
    }

    // First-person arm — hidden while the menu soft-pauses play AND during the camera intro glide
    // (it slides in once the glide completes).
    const build = useGameStore.getState().build;
    const ts = useGameStore.getState().textureState;
    const meta = build.presetMeta[build.presetId];
    updateFirstPersonArm({
      visible: !menuPaused && this.cameraIntroMs === 0,
      buildMode: build.buildMode,
      rotation: meta?.baseRotation,
      parts: meta?.parts,
      texturesReady: ts === 'low' || ts === 'high' || ts === 'off',
      variant: ts === 'high' ? 'hi' : 'lo',
      headBob,
      dtMs: deltaMs,
    });

    // Periodically persist position so an unexpected close still resumes near here.
    this.posSaveAccumMs += deltaMs;
    if (this.posSaveAccumMs >= POS_SAVE_INTERVAL_MS) {
      this.posSaveAccumMs = 0;
      savePlayerPos(this.currentPlayerPose());
    }
  }

  private onResize = (): void => {
    const camera = getCamera();
    if (camera) {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
    }
    // renderScale shrinks the drawing buffer below the display size (fill-rate lever).
    const scale = useGameStore.getState().renderScale;
    const w = Math.max(1, Math.round(window.innerWidth * scale));
    const h = Math.max(1, Math.round(window.innerHeight * scale));
    this.renderer.setSize(w, h, false); // updateStyle=false — keep CSS display size
    const px = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    this.postPipeline?.setSize(px.x, px.y);
  };

  dispose(): void {
    // Stop loops
    this.gameLoop.stop();
    this.playerManager.dispose();

    // Clean up build system
    this.builder.dispose();

    // Clean up spawn manager
    if (this.spawnManager) {
      this.spawnManager.dispose();
    }

    // Clean up voxel terrain
    if (this.voxelIntegration) {
      this.voxelIntegration.dispose();
    }

    disposePostPipeline();

    this.savePlayerPosNow();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pagehide', this.savePlayerPosNow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.renderScaleUnsub) { this.renderScaleUnsub(); this.renderScaleUnsub = null; }
    if (this.gpuTimer) { this.gpuTimer.dispose(); this.gpuTimer = null; }
    if (this.materialSettingsUnsub) { this.materialSettingsUnsub(); this.materialSettingsUnsub = null; }
    if (this.waterSettingsUnsub) { this.waterSettingsUnsub(); this.waterSettingsUnsub = null; }
    this.renderer.dispose();
    document.body.removeChild(this.renderer.domElement);
  }
}
